import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { vec3 } from 'gl-matrix';
import { cmToMm } from '~/utils/units';
import { generateUniqueStructureNameFromList } from '~/domain/services/dicomProcessor';
import { useMarginPreview } from './useMarginPreview';
import { useMarginApplication } from './useMarginApplication';
import {
    ActionButtons,
    LabeledSelect,
    MarginOptions,
    OutputStructureSelect,
    MarginMode,
    MarginDirection,
    getCustomNameError,
} from './MarginToolComponents';
import SymmetricMarginControls from './SymmetricMarginControls';
import AsymmetricMarginControls from './AsymmetricMarginControls';
import { getDefaultMarginName, getStructureColor } from './marginToolbarUtils';
import OperationContentShell, {
    OperationShellRenderProps,
} from '../shared/OperationContentShell';

type AsymmetricMargins = {
    left: number;
    right: number;
    anterior: number;
    posterior: number;
    superior: number;
    inferior: number;
};

const INITIAL_ASYMMETRIC: AsymmetricMargins = {
    left: 0,
    right: 0,
    anterior: 0,
    posterior: 0,
    superior: 0,
    inferior: 0,
};

interface MarginOperationContentProps {
    mode: 'symmetric' | 'asymmetric';
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

export default function MarginOperationContent({
    mode,
    onClose,
    onPreventCloseChange,
}: MarginOperationContentProps) {
    return (
        <OperationContentShell>
            {(shellProps) => (
                <MarginFormContent
                    key={mode}
                    mode={mode}
                    onClose={onClose}
                    onPreventCloseChange={onPreventCloseChange}
                    {...shellProps}
                />
            )}
        </OperationContentShell>
    );
}

// Inner component that has access to a valid inputRoi —
// hooks need stable references and can't be called conditionally.
interface MarginFormContentProps extends OperationShellRenderProps {
    mode: 'symmetric' | 'asymmetric';
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

function MarginFormContent({
    mode,
    onClose,
    onPreventCloseChange,
    environment,
    inputRoi,
    structures,
    onInputROIChange,
}: MarginFormContentProps) {
    // Form state
    const [currentMode, setCurrentMode] = useState<MarginMode>(mode);
    const [marginType, setMarginType] = useState<MarginDirection>('outer');
    const [margin, setMargin] = useState(0.5);
    const [asymmetric, setAsymmetric] =
        useState<AsymmetricMargins>(INITIAL_ASYMMETRIC);
    const [outputRoiNumber, setOutputRoiNumber] = useState<number | null>(null);
    const [customRoiName, setCustomRoiName] = useState('');

    // Preview and application hooks
    const preview = useMarginPreview({ environment, inputRoi });
    const cleanupRef = useRef(preview.cleanup);
    cleanupRef.current = preview.cleanup;

    const handleClose = useCallback(() => {
        preview.cleanup();
        onClose();
    }, [preview, onClose]);

    const apply = useMarginApplication({
        environment,
        inputRoi,
        onSuccess: handleClose,
        updateView: preview.updateView,
    });

    // Flow isPreviewing state to parent for close-blocking
    const isPreventing = preview.isPreviewing || apply.isProcessing;
    useEffect(() => {
        onPreventCloseChange(isPreventing);
    }, [isPreventing, onPreventCloseChange]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupRef.current();
        };
    }, []);

    // Computed values
    const currentInputName = inputRoi.name;

    const inputColor = useMemo(
        () =>
            getStructureColor(
                structures.find((s) => s.name === currentInputName)?.color || {
                    R: Math.round(inputRoi.color[0] * 255),
                    G: Math.round(inputRoi.color[1] * 255),
                    B: Math.round(inputRoi.color[2] * 255),
                }
            ),
        [structures, currentInputName, inputRoi.color]
    );

    const outputColor = useMemo(
        () =>
            outputRoiNumber === null
                ? undefined
                : getStructureColor(
                      structures.find((s) => s.roiNumber === outputRoiNumber)
                          ?.color
                  ),
        [structures, outputRoiNumber]
    );

    const customNameError = useMemo(
        () =>
            getCustomNameError(
                customRoiName,
                structures.map((s) => s.name)
            ),
        [customRoiName, structures]
    );

    const structureOptions = useMemo(
        () =>
            structures.map((s) => ({
                roiNumber: s.roiNumber,
                label: s.name,
                color: getStructureColor(s.color),
            })),
        [structures]
    );

    const getActual = useCallback(
        (v: number) => (marginType === 'inner' ? -v : v),
        [marginType]
    );

    // Compute margins in LPS orientation for SDFOperations:
    // negLps = [Right (-X), Anterior (-Y), Inferior (-Z)]
    // posLps = [Left (+X), Posterior (+Y), Superior (+Z)]
    const computeVec3s = useCallback(() => {
        const { left, right, anterior, posterior, superior, inferior } =
            asymmetric;
        return {
            negLpsMm: vec3.fromValues(
                cmToMm(getActual(right)),
                cmToMm(getActual(anterior)),
                cmToMm(getActual(inferior))
            ),
            posLpsMm: vec3.fromValues(
                cmToMm(getActual(left)),
                cmToMm(getActual(posterior)),
                cmToMm(getActual(superior))
            ),
        };
    }, [asymmetric, getActual]);

    const existingNames = useMemo(
        () => structures.map((s) => s.name),
        [structures]
    );

    const handleApply = useCallback(() => {
        const newName =
            outputRoiNumber === null
                ? generateUniqueStructureNameFromList(
                      existingNames,
                      customRoiName.trim() ||
                          getDefaultMarginName(currentInputName)
                  )
                : undefined;

        if (currentMode === 'symmetric') {
            apply.handleSymmetricApply(
                cmToMm(getActual(margin)),
                outputRoiNumber,
                newName
            );
        } else {
            const { negLpsMm, posLpsMm } = computeVec3s();
            apply.handleAsymmetricApply(
                negLpsMm,
                posLpsMm,
                outputRoiNumber,
                newName
            );
        }
    }, [
        outputRoiNumber,
        customRoiName,
        currentInputName,
        existingNames,
        currentMode,
        margin,
        getActual,
        computeVec3s,
        apply,
    ]);

    const handleReset = useCallback(() => {
        if (currentMode === 'symmetric') {
            setMargin(0);
        } else {
            setAsymmetric(INITIAL_ASYMMETRIC);
        }
    }, [currentMode]);

    // Auto-trigger preview with debounce when margin values change
    const AUTO_PREVIEW_DELAY_MS = 500;
    useEffect(() => {
        const allZero =
            currentMode === 'symmetric'
                ? margin === 0
                : Object.values(asymmetric).every((v) => v === 0);

        if (allZero || inputRoi.isEmpty) {
            preview.cleanup();
            return;
        }

        const timeout = setTimeout(() => {
            if (currentMode === 'symmetric') {
                preview.handlePreviewSymmetric(cmToMm(getActual(margin)));
            } else {
                const { negLpsMm, posLpsMm } = computeVec3s();
                preview.handlePreviewAsymmetric(negLpsMm, posLpsMm);
            }
        }, AUTO_PREVIEW_DELAY_MS);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        margin,
        asymmetric,
        marginType,
        currentMode,
        inputRoi,
        getActual,
        computeVec3s,
        preview,
    ]);

    const updateAsymmetric = useCallback(
        (key: keyof AsymmetricMargins) => (value: number) => {
            setAsymmetric((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    // customNameError only blocks Apply when the user is creating a new
    // structure (outputRoiNumber === null). Switching to an existing output
    // while a stale custom name is in the text box must not keep Apply disabled.
    const isCustomNameInvalid = outputRoiNumber === null && !!customNameError;

    return (
        <>
            <LabeledSelect
                label="Input Structure"
                value={inputRoi.roiNumber}
                onChange={onInputROIChange}
                options={structureOptions}
                colorDot={inputColor}
            />

            <OutputStructureSelect
                outputRoiNumber={outputRoiNumber}
                setOutputRoiNumber={setOutputRoiNumber}
                outputColor={outputColor}
                inputColor={inputColor}
                inputRoiNumber={inputRoi.roiNumber}
                availableStructures={structures}
                customRoiName={customRoiName}
                setCustomRoiName={setCustomRoiName}
                customNameError={customNameError}
            />

            <MarginOptions
                marginType={marginType}
                setMarginType={setMarginType}
                currentMode={currentMode}
                setCurrentMode={setCurrentMode}
                onReset={handleReset}
            />

            {currentMode === 'symmetric' ? (
                <SymmetricMarginControls
                    margin={margin}
                    onMarginChange={setMargin}
                />
            ) : (
                <AsymmetricMarginControls
                    leftMargin={asymmetric.left}
                    rightMargin={asymmetric.right}
                    anteriorMargin={asymmetric.anterior}
                    posteriorMargin={asymmetric.posterior}
                    superiorMargin={asymmetric.superior}
                    inferiorMargin={asymmetric.inferior}
                    onLeftMarginChange={updateAsymmetric('left')}
                    onRightMarginChange={updateAsymmetric('right')}
                    onAnteriorMarginChange={updateAsymmetric('anterior')}
                    onPosteriorMarginChange={updateAsymmetric('posterior')}
                    onSuperiorMarginChange={updateAsymmetric('superior')}
                    onInferiorMarginChange={updateAsymmetric('inferior')}
                />
            )}

            {inputRoi.isEmpty && (
                <Typography
                    fontSize={10}
                    color="warning.main"
                    fontStyle="italic"
                >
                    Selected input structure is empty. Margin cannot be applied.
                </Typography>
            )}

            {inputRoi.volumeCm3 !== undefined && inputRoi.volumeCm3 > 100 && (
                <Typography
                    fontSize={10}
                    color="text.secondary"
                    fontStyle="italic"
                >
                    Preview may take longer for large structures (
                    {inputRoi.volumeCm3.toFixed(1)} cm³)
                </Typography>
            )}

            <ActionButtons
                onApply={handleApply}
                isApplyDisabled={isCustomNameInvalid || inputRoi.isEmpty}
            />
        </>
    );
}
